import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { apiPost, apiDelete } from '../../utils/api';
import UserAutocomplete from '../UserAutocomplete';
import ConfirmDialog from '../ConfirmDialog';
import useSubmitGuard from '../../hooks/useSubmitGuard';

/**
 * A gag's TEAM — the people working on that one gag (Gags/teams/tasks
 * Stage 1, owner sign-off 2026-10-02: *"Team = the people on a gag"*; never
 * "Crew", which keeps meaning the whole department). The OWNER leads it and
 * is shown first; the members are `gag_team_members`.
 *
 * Everyone in the department READS it (rule 1). Adding / removing is offered
 * only when the SERVER says the caller may (`canManage` ← the gag detail's
 * `can_assign_tasks`, rule 2: the gag owner or breakdown rights) — never
 * re-derived here; the server re-checks every write. Removing a member keeps
 * their task assignments (the server's rule), so the confirm says so.
 *
 * `addSlot` (OPTIONAL, a DOM node): the add input is portalled into it — the
 * gag panel puts it on the task board's tab line, where a search box would sit
 * (owner 2026-10-12). `addSlot` passed as `null` (slot not mounted yet) renders
 * no input rather than a second one here; omitted → inline, as before.
 */
export default function GagTeamSection({ gagId, ownerId, ownerName, members, canManage, departmentUsers, onChanged, addSlot }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // { user_id, display_name }
  const { guard } = useSubmitGuard();

  const onTeam = new Set([ownerId, ...members.map((m) => m.user_id)].filter((id) => id != null).map(Number));
  const addable = (departmentUsers || []).filter((u) => !onTeam.has(Number(u.id)));

  const run = (fn) => guard(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Could not change the team (HTTP ${res.status})`);
      } else {
        await onChanged?.();
      }
    } catch {
      setError('Could not change the team — check your connection.');
    }
    setBusy(false);
  });

  const add = (userId) => run(() => apiPost(`/api/gags/${gagId}/team`, { user_id: userId }));
  const remove = (member) => run(async () => {
    const res = await apiDelete(`/api/gags/${gagId}/team/${member.user_id}`);
    setConfirmRemove(null);
    return res;
  });

  const addControl = (width) => (
    <div className={width} data-testid="gag-team-add">
      <UserAutocomplete
        value=""
        options={addable}
        clearOnSelect
        ariaLabel="Add someone to this gag's team"
        placeholder="Add someone to the team…"
        onChange={(_name, uid) => { if (uid != null) add(uid); }}
      />
    </div>
  );

  return (
    <div className="space-y-1.5" data-testid="gag-team-section">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 mr-1">Team</span>
        {ownerId != null ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200"
            data-testid="gag-team-owner">
            {ownerName || 'Owner'} <span className="ml-1 text-indigo-600 dark:text-indigo-300">· leads</span>
          </span>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400">No owner yet</span>
        )}
        {members.map((m) => (
          <span key={m.user_id}
            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
            data-testid={`gag-team-member-${m.user_id}`}>
            {m.display_name}
            {canManage && (
              <button type="button" disabled={busy} onClick={() => setConfirmRemove(m)}
                aria-label={`Remove ${m.display_name} from the team`}
                className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-600">
                <span aria-hidden="true">✕</span>
              </button>
            )}
          </span>
        ))}
        {members.length === 0 && ownerId != null && (
          <span className="text-xs text-gray-500 dark:text-gray-400">Nobody else yet</span>
        )}
      </div>
      {canManage && addSlot === undefined && addControl('max-w-xs')}
      {canManage && addSlot && createPortal(addControl('w-full'), addSlot)}
      {error && (
        <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800" role="alert">
          {error}
        </div>
      )}
      <ConfirmDialog
        open={!!confirmRemove}
        title="Remove from the team?"
        message={confirmRemove ? `${confirmRemove.display_name} will no longer be on this gag's team. Any tasks already assigned to them stay assigned.` : ''}
        confirmLabel="Remove"
        variant="danger"
        busy={busy}
        onConfirm={() => remove(confirmRemove)}
        onCancel={() => setConfirmRemove(null)}
      />
    </div>
  );
}
